import type { PaymentPlan, PaymentPlanInstalment } from '@/api/types/PatientApi'
import { format } from 'date-fns'

export const SETTLED = new Set([
	'SUCCEEDED',
	'REFUNDED',
	'PARTIALLY_REFUNDED',
	'CANCELED',
])
export const ATTENTION = new Set(['FAILED', 'EXPIRED', 'DISPUTED'])

export type PlanInstalment = {
	plan: PaymentPlan
	instalment: PaymentPlanInstalment
	/** 1-based position within its plan, and the plan's length. */
	index: number
	count: number
}

export const isSettled = (i: PaymentPlanInstalment) =>
	i.paymentStatus !== null && SETTLED.has(i.paymentStatus)

export const isOverdue = (i: PaymentPlanInstalment, today: string) =>
	i.dueOn.slice(0, 10) < today && !isSettled(i)

export const needsAttention = (i: PaymentPlanInstalment, today: string) =>
	(i.paymentStatus !== null && ATTENTION.has(i.paymentStatus)) || isOverdue(i, today)

/** Every instalment of every plan, numbered within its plan. */
export const allInstalments = (plans: PaymentPlan[]): PlanInstalment[] =>
	plans.flatMap((plan) =>
		plan.instalments.map((instalment, i) => ({
			plan,
			instalment,
			index: i + 1,
			count: plan.instalments.length,
		}))
	)

/** Instalments still to be paid, soonest first. */
export const upcomingInstalments = (plans: PaymentPlan[]) =>
	allInstalments(plans)
		.filter(({ instalment }) => !isSettled(instalment))
		.sort((a, b) => a.instalment.dueOn.localeCompare(b.instalment.dueOn))

/**
 * What a plan with nothing left to pay is called, read off its instalments rather than its own
 * status flag.
 *
 * The flag lags: `SETTLED` counts a cancelled instalment as settled, so a plan whose payments were
 * all cancelled has no next due and lands in the finished group while `plan.status` still says
 * `DELINQUENT` — which is how a row came to read "Behind" under a heading that said "Paid off".
 * The instalments are the truth here, as they already are for the row's own mark.
 */
export const settledLine = (plan: PaymentPlan) => {
	const statuses = plan.instalments.map((i) => i.paymentStatus)
	if (statuses.length > 0 && statuses.every((status) => status === 'CANCELED')) {
		return 'Cancelled'
	}
	if (
		statuses.some((status) => status === 'REFUNDED' || status === 'PARTIALLY_REFUNDED')
	) {
		return 'Refunded'
	}
	return 'Paid off'
}

export const paidCount = (plan: PaymentPlan) => plan.instalments.filter(isSettled).length

export const nextDue = (plan: PaymentPlan) =>
	[...plan.instalments]
		.filter((i) => !isSettled(i))
		.sort((a, b) => a.dueOn.localeCompare(b.dueOn))[0]

/** "Due Aug 1" for what is to come, "Overdue since Aug 1" for what is late. */
export const dueLine = (i: PaymentPlanInstalment, today: string) =>
	`${isOverdue(i, today) ? 'Overdue since' : 'Due'} ${format(i.dueOn, 'MMM d')}`

/** "Payment 2 of 4" */
export const positionLine = (index: number, count: number) =>
	`Payment ${index} of ${count}`

export const today = () => format(new Date(), 'yyyy-MM-dd')
